import React from "react";
import { Handle, Position } from "reactflow";
import { Box, Typography, Chip, Tooltip } from "@mui/material";
import QuestionAnswerOutlined from "@mui/icons-material/QuestionAnswerOutlined";
import SmartToyOutlined from "@mui/icons-material/SmartToyOutlined";

/**
 * Nó do fluxo do ChatBot (padrão visual das melhores plataformas).
 * Exibe identificador, preview da resposta e badges (Exceção, botões, etc.).
 */
const ChatbotNode = ({ data, selected }) => {
  const isExcecao = data?.identificador === "Exceção";
  const isInicio = data?.identificador === "Início";
  const isGlobal = !!data?.global;
  const ignoresGlobal = !!data?.ignorarglobal;
  const hasButtons = Array.isArray(data?.botoes) ? data.botoes.length > 0 : false;
  const label = data?.identificador || data?.nome || "Nó";
  const fullPreview = data?.resposta ? String(data.resposta).replace(/\n/g, " ").trim() : "";
  const preview = fullPreview ? fullPreview.slice(0, 56) + (fullPreview.length > 56 ? "…" : "") : null;
  const showTooltip = (fullPreview && fullPreview.length > 56) || (label && label.length > 20);
  const excecaoTooltip = "Acionado quando nenhuma das regras ou opções deste nível for atendida (resposta fora do esperado).";

  const content = (
    <Box
      sx={{
        minWidth: 220,
        maxWidth: 300,
        p: 2,
        bgcolor: selected ? "primary.light" : "background.paper",
        color: selected ? "primary.contrastText" : "text.primary",
        borderRadius: 2,
        border: "2px solid",
        borderColor: selected ? "primary.main" : isGlobal ? "primary.main" : "divider",
        borderStyle: isGlobal && !selected ? "dashed" : "solid",
        boxShadow: selected ? 3 : 2,
        transition: "box-shadow 0.2s ease, border-color 0.2s ease",
      }}
    >
      <Handle type="target" position={Position.Left} style={{ width: 10, height: 10, left: -2 }} />
      <Box display="flex" alignItems="center" gap={1} mb={0.75}>
        {isExcecao ? (
          <SmartToyOutlined sx={{ fontSize: 20, opacity: 0.9, flexShrink: 0 }} />
        ) : (
          <QuestionAnswerOutlined sx={{ fontSize: 20, opacity: 0.9, flexShrink: 0 }} />
        )}
        <Typography
          variant="subtitle2"
          fontWeight={600}
          noWrap
          sx={{ minWidth: 0 }}
          title={label}
        >
          {label}
        </Typography>
        {isExcecao && (
          <Chip label="Exceção" size="small" sx={{ height: 18, fontSize: "0.7rem", flexShrink: 0 }} />
        )}
        {isInicio && (
          <Chip label="Início" size="small" color="primary" sx={{ height: 18, fontSize: "0.7rem", flexShrink: 0 }} />
        )}
        {isGlobal && !isExcecao && !isInicio && (
          <Chip label="Global" size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: "0.7rem", flexShrink: 0 }} />
        )}
        {ignoresGlobal && !isExcecao && (
          <Chip label="Ignora globais" size="small" sx={{ height: 18, fontSize: "0.65rem", flexShrink: 0 }} />
        )}
      </Box>
      {preview && (
        <Typography
          variant="caption"
          display="block"
          sx={{ opacity: 0.85, lineHeight: 1.4 }}
          noWrap
          title={fullPreview}
        >
          {preview}
        </Typography>
      )}
      {hasButtons && (
        <Typography variant="caption" sx={{ opacity: 0.7, display: "block", mt: 0.25 }}>
          {data.botoes.length} botão(ões)
        </Typography>
      )}
      <Handle type="source" position={Position.Right} style={{ width: 10, height: 10, right: -2 }} />
    </Box>
  );

  const tooltipTitle = isExcecao
    ? excecaoTooltip
    : showTooltip
      ? (() => {
          const tooltipParts = [];
          if (label && label.length > 20) tooltipParts.push(label);
          if (fullPreview && fullPreview.length > 56) tooltipParts.push(fullPreview);
          return tooltipParts.length === 1 ? tooltipParts[0] : <Box sx={{ "& > div": { mt: 0.5 } }}>{tooltipParts.map((t, i) => <div key={i}>{t}</div>)}</Box>;
        })()
      : null;

  if (tooltipTitle) {
    return (
      <Tooltip
        title={tooltipTitle}
        placement="top"
        enterDelay={isExcecao ? 200 : 400}
        leaveDelay={0}
      >
        {content}
      </Tooltip>
    );
  }
  return content;
};

export default ChatbotNode;
